import {
  S3HttpAdapter,
  AxiosS3HttpAdapter,
  isS3HttpError
} from 'infra/s3/s3-http-adapter'
import { getUploadLogger, UploadLogger } from 'common/utils/upload-logger'

const logger = getUploadLogger()

export class S3UploadService {
  constructor(
    private readonly httpAdapter: S3HttpAdapter = new AxiosS3HttpAdapter(),
    private readonly uploadLogger: UploadLogger = logger
  ) {}

  async uploadToS3(
    presignedUrl: string,
    file: File,
    mimeType: string
  ): Promise<void> {
    try {
      await this.httpAdapter.put(presignedUrl, file, mimeType)
    } catch (error) {
      this.uploadLogger.error('Erro ao enviar arquivo para S3', error)

      if (error instanceof S3UploadError) {
        throw error
      }

      if (isS3HttpError(error)) {
        if (error.isNetworkError) {
          throw new S3UploadError(
            'NETWORK_ERROR',
            'CORS error or network issue during S3 upload. The S3 bucket may not be configured to accept requests from this origin.',
            undefined,
            error.cause instanceof Error ? error.cause : undefined
          )
        }

        const statusCode = error.status

        if (statusCode === 403) {
          throw new S3UploadError(
            'URL_EXPIRED',
            'Presigned URL has expired. Please request a new URL.',
            statusCode
          )
        }
        if (statusCode === 400) {
          throw new S3UploadError(
            'INVALID_CONTENT_TYPE',
            'Content-Type does not match presigned URL configuration.',
            statusCode
          )
        }
        throw new S3UploadError(
          'UNKNOWN_ERROR',
          `S3 upload failed with status ${statusCode ?? 'unknown'}: ${
            error.statusText ?? ''
          }`,
          statusCode
        )
      }

      this.uploadLogger.error('Erro desconhecido no upload para S3', error, {
        fileName: file.name
      })
      throw new S3UploadError(
        'UNKNOWN_ERROR',
        `Unexpected error during S3 upload: ${
          error instanceof Error ? error.message : String(error)
        }`,
        undefined,
        error instanceof Error ? error : undefined
      )
    }
  }
}

export class S3UploadError extends Error {
  constructor(
    public readonly code:
      | 'URL_EXPIRED'
      | 'INVALID_CONTENT_TYPE'
      | 'NETWORK_ERROR'
      | 'UNKNOWN_ERROR',
    message: string,
    public readonly statusCode?: number,
    public readonly originalError?: Error
  ) {
    super(message)
    this.name = 'S3UploadError'

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, S3UploadError)
    }
  }
}
